import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { useDismissOnOutside } from '../lib/useDismissOnOutside';

export interface RowMenuItem { key: string; label: ReactNode; onSelect: () => void; danger?: boolean; disabled?: boolean }

/**
 * A ⋯ button with a short menu of row actions (1.2.1, the Jobs list). The menu is placed on the page (fixed), so a
 * table that scrolls sideways does not cut it off; scrolling or Escape closes it.
 */
export function RowMenu({ items, label, testId }: { items: RowMenuItem[]; label: string; testId?: string }) {
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const anchor = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  useDismissOnOutside(anchor, at !== null, () => setAt(null));
  useEffect(() => {
    if (!at) return;
    const close = () => setAt(null);
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { setAt(null); button.current?.focus(); } };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', key);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); document.removeEventListener('keydown', key); };
  }, [at]);
  if (items.length === 0) return null;
  const open = () => {
    const r = button.current!.getBoundingClientRect();
    setAt({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
  };
  return (
    <div className="row-menu" ref={anchor}>
      <button ref={button} type="button" className="btn ghost sm icon" aria-label={label} aria-haspopup="menu" aria-expanded={at !== null}
        onClick={() => (at ? setAt(null) : open())} data-testid={testId}><MoreHorizontal size={14} /></button>
      {at && (
        <div className="row-menu-list" role="menu" style={{ top: at.top, right: at.right }} data-testid={testId ? `${testId}-list` : undefined}>
          {items.map((i) => (
            <button key={i.key} type="button" role="menuitem" className={`row-menu-item${i.danger ? ' danger' : ''}`} disabled={i.disabled} data-action={i.key}
              onClick={() => { setAt(null); i.onSelect(); }}>{i.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}
